import React, { useCallback, useEffect, useState } from "react";
import api from "../../hooks/api";
import { API_URLS } from "../../App";
import {
  ProviderRouteConfig,
  TenantProviderConfig,
  AvailabilityRule,
  PAYMENT_METHOD_LABELS,
  PROVIDER_LABELS,
} from "../../model/SitePayment";
import PaymentMethodForm from "./PaymentMethodForm";
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight } from "lucide-react";

const formatRule = (rule: AvailabilityRule): string => {
  switch (rule.type) {
    case "authenticated":
      return "Auth";
    case "nif":
      return rule.kind === "company"
        ? "NIF Empresa"
        : rule.kind === "individual"
          ? "NIF Particular"
          : "NIF PT";
    case "nif_prefix":
      return `NIF prefixo ${(rule.prefixes ?? []).join(", ")}`;
    default:
      return rule.type;
  }
};

const PaymentMethodList: React.FC = () => {
  const [routes, setRoutes] = useState<ProviderRouteConfig[]>([]);
  const [providers, setProviders] = useState<TenantProviderConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingRoute, setEditingRoute] = useState<ProviderRouteConfig | null>(
    null,
  );
  const [showForm, setShowForm] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [routesRes, providersRes] = await Promise.all([
        api.get(API_URLS.PAYMENT_ROUTES),
        api.get(API_URLS.PAYMENT_PROVIDER_CONFIGS),
      ]);
      setRoutes(routesRes.data?.data || routesRes.data || []);
      setProviders(providersRes.data?.data || providersRes.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao carregar metodos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleToggleEnabled = async (route: ProviderRouteConfig) => {
    try {
      await api.put(API_URLS.PAYMENT_ROUTES, {
        payment_method_type: route.payment_method_type,
        provider_name: route.provider_name,
        is_enabled: !route.is_enabled,
        priority: route.priority,
        display_name: route.display_name,
        description: route.description,
        icon: route.icon,
        availability_rules: route.availability_rules,
      });
      await fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao atualizar estado.");
    }
  };

  const handleDelete = async (route: ProviderRouteConfig) => {
    if (
      !confirm(
        `Eliminar metodo "${route.display_name || route.payment_method_type}"?`,
      )
    )
      return;
    try {
      await api.delete(`${API_URLS.PAYMENT_ROUTES}/${route.id}`);
      await fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.error || "Erro ao eliminar.");
    }
  };

  const handleFormClose = (saved: boolean) => {
    setShowForm(false);
    setEditingRoute(null);
    if (saved) fetchData();
  };

  if (loading) return <div className="text-gray-500 py-4">A carregar...</div>;

  return (
    <div>
      {error && (
        <div className="bg-red-50 text-red-700 px-4 py-2 rounded mb-4 text-sm">
          {error}
        </div>
      )}

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-800">
          Metodos de Pagamento
        </h2>
        <button
          className="button is-primary is-small flex items-center gap-1"
          onClick={() => {
            setEditingRoute(null);
            setShowForm(true);
          }}
        >
          <Plus className="w-4 h-4" /> Adicionar
        </button>
      </div>

      {routes.length === 0 ? (
        <p className="text-gray-500 text-sm">
          Nenhum metodo de pagamento configurado.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="py-2 px-3">Metodo</th>
                <th className="py-2 px-3">Fornecedor</th>
                <th className="py-2 px-3">Nome Exibicao</th>
                <th className="py-2 px-3">Prioridade</th>
                <th className="py-2 px-3">Regras</th>
                <th className="py-2 px-3">Estado</th>
                <th className="py-2 px-3 text-right">Acoes</th>
              </tr>
            </thead>
            <tbody>
              {routes
                .sort((a, b) => b.priority - a.priority)
                .map((r) => (
                  <tr key={r.id} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium">
                      {PAYMENT_METHOD_LABELS[r.payment_method_type] ||
                        r.payment_method_type}
                    </td>
                    <td className="py-2 px-3">
                      {PROVIDER_LABELS[r.provider_name] || r.provider_name}
                    </td>
                    <td className="py-2 px-3 text-gray-600">
                      {r.display_name || "-"}
                    </td>
                    <td className="py-2 px-3">{r.priority}</td>
                    <td className="py-2 px-3">
                      <div className="flex flex-wrap gap-1">
                        {(r.availability_rules ?? []).length === 0 ? (
                          <span className="text-gray-400 text-xs">-</span>
                        ) : (
                          (r.availability_rules ?? []).map((rule, i) => (
                            <span
                              key={i}
                              className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                            >
                              {formatRule(rule)}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <button
                        onClick={() => handleToggleEnabled(r)}
                        className="flex items-center gap-1"
                        title={r.is_enabled ? "Desativar" : "Ativar"}
                      >
                        {r.is_enabled ? (
                          <ToggleRight className="w-5 h-5 text-green-600" />
                        ) : (
                          <ToggleLeft className="w-5 h-5 text-gray-400" />
                        )}
                        <span
                          className={
                            r.is_enabled ? "text-green-600" : "text-gray-400"
                          }
                        >
                          {r.is_enabled ? "Ativo" : "Inativo"}
                        </span>
                      </button>
                    </td>
                    <td className="py-2 px-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          className="text-blue-600 hover:text-blue-800"
                          title="Editar"
                          onClick={() => {
                            setEditingRoute(r);
                            setShowForm(true);
                          }}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          className="text-red-500 hover:text-red-700"
                          title="Eliminar"
                          onClick={() => handleDelete(r)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <PaymentMethodForm
          route={editingRoute}
          providers={providers}
          onClose={handleFormClose}
        />
      )}
    </div>
  );
};

export default PaymentMethodList;
